// src/components/chat/tabs/HelpTab.tsx
'use client';

import { Search, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useChatStore } from '@/store/useChatStore';
import { useState } from 'react';

export function HelpTab() {
    const { toggleChat } = useChatStore();
    const [searchQuery, setSearchQuery] = useState('');

    const articles = [
        { title: "What is Cherie Childminding?", category: "Getting Started", href: "/about" },
        { title: "How do I book childcare?", category: "Getting Started", href: "/contact" },
        { title: "What are your hours?", category: "Services", href: "/services" },
        { title: "Safety and policies", category: "Policies", href: "/policies" },
        { title: "Daily routine and activities", category: "Services", href: "/daily-routine" },
        { title: "Pricing and payment", category: "Services", href: "/services" },
    ];

    // Filter articles based on search query
    const filteredArticles = articles.filter(article =>
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.category.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="p-4 space-y-6 overflow-y-auto h-full">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold text-[var(--color-secondary)] mb-2">Help Center</h2>
                <p className="text-gray-600">Find answers to common questions</p>
            </div>

            {/* Search Bar */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for help..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                />
            </div>

            {/* Articles */}
            <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    {searchQuery ? `Search Results (${filteredArticles.length})` : 'Articles'}
                </h3>
                {filteredArticles.length > 0 ? (
                    <div className="space-y-2">
                        {filteredArticles.map((article, index) => (
                            <Link
                                key={index}
                                href={article.href}
                                onClick={() => toggleChat()}
                                className="w-full p-4 bg-white border border-gray-200 rounded-lg hover:border-[var(--color-primary)] hover:shadow-sm transition-all text-left group flex items-center justify-between block"
                            >
                                <div>
                                    <h4 className="font-medium text-sm text-gray-900 group-hover:text-[var(--color-primary)] mb-1">
                                        {article.title}
                                    </h4>
                                    <p className="text-xs text-gray-500">{article.category}</p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[var(--color-primary)]" />
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No articles found for "{searchQuery}"</p>
                        <p className="text-xs mt-1">Try a different search term or browse all articles</p>
                    </div>
                )}
            </div>

            {/* Contact Support */}
            <div className="border-t pt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Still need help?</h3>
                <button className="w-full p-4 bg-[var(--color-primary)] text-white rounded-lg font-semibold hover:bg-[var(--color-primary-dark)] transition-colors">
                    Contact Support
                </button>
            </div>
        </div>
    );
}
